import { Chip } from "@material-tailwind/react";
import {
    AVAILABILITY_STATUSES,
    DAY_PASSES_TYPES,
    DEAL_STAGES,
    LEVELS,
    OPERATIONAL_STATUSES,
    PAYMENT_STATUS,
    SUPPORT_TICKET_PRIORITIES,
    SUPPORT_TICKET_STATUSES,
    VERIFICATION_TYPES
} from "./enums";
import { convertToPresentableText } from "./functions";
import {
    AlertCircleIcon,
    AlertTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    InfoIcon,
    MessageSquareIcon,
    PlusIcon,
    SettingsIcon,
    TagIcon,
    TrendingUpIcon,
    UserIcon,
    XCircleIcon,
    ZapIcon
} from "lucide-react";

export const InterpretOperationalStatus = (status, type = "chip") => {
    switch (status) {
        case OPERATIONAL_STATUSES.OPERATIONAL:
            return type === "chip" ? <Chip color="green" size="sm" variant="ghost" value="Operational" /> : <span className="text-green-700 text-xs">Operational</span>;
        case OPERATIONAL_STATUSES.IN_MAINTENANCE:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="In Maintenance" /> : <span className="text-red-500 text-xs">In Maintenance</span>;
        case OPERATIONAL_STATUSES.UNDER_CONSTRUCTION:
            return type === "chip" ? <Chip color="yellow" size="sm" variant="ghost" value="Under Construction" /> : <span className="text-yellow-500 text-xs">Under Construction</span>;
        case OPERATIONAL_STATUSES.CLOSED:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Closed" /> : <span className="text-gray-500 text-xs">Closed</span>;
        default:
            return type === "chip" ? <Chip color="brown" size="sm" variant="ghost" value="Unknown" /> : <span className="text-brown-500 text-xs">Unknown</span>;
    }
};

export const InterpretLeadStatus = (status, type = "chip", customText = "", customColor = "") => {
    const colors = { bg: "bg-gray-50", text: "text-gray-500" };
    switch (status) {
        case DEAL_STAGES.WON:
            colors.bg = "green";
            colors.text = "text-green-700";
            break;
        case DEAL_STAGES.CONTACTED:
            colors.bg = "indigo";
            colors.text = "text-indigo-700";
            break;
        case DEAL_STAGES.QUALIFIED:
            colors.bg = "purple";
            colors.text = "text-purple-700";
            break;
        case DEAL_STAGES.NEGOTIATING:
            colors.bg = "pink";
            colors.text = "text-pink-700";
            break;
        case DEAL_STAGES.NEW:
            colors.bg = "blue";
            colors.text = "text-blue-700";
            break;
        case DEAL_STAGES.OPEN:
            colors.bg = "teal";
            colors.text = "text-teal-700";
            break;
        case DEAL_STAGES.DO_NOT_CONTACT:
            colors.bg = "deep-orange";
            colors.text = "text-deep-orange-700";
            break;
        case DEAL_STAGES.RECRUITMENT:
            colors.bg = "cyan";
            colors.text = "text-cyan-700";
            break;
        default:
            colors.bg = "gray";
            colors.text = "text-gray-500";
            break;
    }
    if (type === "chip") {
        return <Chip variant="ghost" color={customColor || colors.bg} value={customText || convertToPresentableText(status)} />;
    }
    return <span className={customColor || colors.text}>{customText || convertToPresentableText(status)}</span>;
};

export const InterpretEngagementLevel = (level, type = "chip") => {
    switch (level) {
        case LEVELS.LOW:
            return type === "chip" ? <Chip color="blue" size="sm" variant="ghost" value="Low" /> : <span className="text-blue-700 text-xs">Low</span>;
        case LEVELS.MEDIUM:
            return type === "chip" ? <Chip color="indigo" size="sm" variant="ghost" value="Medium" /> : <span className="text-indigo-700 text-xs">Medium</span>;
        case LEVELS.HIGH:
            return type === "chip" ? <Chip color="purple" size="sm" variant="ghost" value="High" /> : <span className="text-purple-700 text-xs">High</span>;
        default:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Unknown" /> : <span className="text-gray-500 text-xs">Unknown</span>;
    }
};

export const InterpretActiveStatus = (status, type = "chip") => {
    switch (status) {
        case VERIFICATION_TYPES.APPROVED:
            return type === "chip" ? <Chip color="green" size="sm" variant="ghost" value="Approved" /> : <span className="text-green-700 text-xs">Approved</span>;
        case VERIFICATION_TYPES.PENDING:
            return type === "chip" ? <Chip color="yellow" size="sm" variant="ghost" value="Pending" /> : <span className="text-yellow-700 text-xs">Pending</span>;
        case VERIFICATION_TYPES.REJECTED:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="Rejected" /> : <span className="text-red-700 text-xs">Rejected</span>;
        case VERIFICATION_TYPES.CANCELLED:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Cancelled" /> : <span className="text-gray-500 text-xs">Cancelled</span>;
        case VERIFICATION_TYPES.COMPLETED:
            return type === "chip" ? <Chip color="blue" size="sm" variant="ghost" value="Completed" /> : <span className="text-blue-700 text-xs">Completed</span>;
        default:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Unknown" /> : <span className="text-gray-500 text-xs">Unknown</span>;
    }
};

export const InterpretAvailabilityStatus = (status, type = "chip") => {
    switch (status) {
        case AVAILABILITY_STATUSES.AVAILABLE:
            return type === "chip" ? <Chip color="green" size="sm" variant="ghost" value="Available" /> : <span className="text-green-700 text-xs">Available</span>;
        case AVAILABILITY_STATUSES.OCCUPIED:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="Occupied" /> : <span className="text-red-700 text-xs">Occupied</span>;
        case AVAILABILITY_STATUSES.RESERVED:
            return type === "chip" ? <Chip color="yellow" size="sm" variant="ghost" value="Reserved" /> : <span className="text-yellow-700 text-xs">Reserved</span>;
        case AVAILABILITY_STATUSES.IN_MAINTENANCE:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="In Maintenance" /> : <span className="text-gray-500 text-xs">In Maintenance</span>;
        case AVAILABILITY_STATUSES.BLOCKED:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="Blocked" /> : <span className="text-red-700 text-xs">Blocked</span>;
        default:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Unknown" /> : <span className="text-gray-500 text-xs">Unknown</span>;
    }
};
export const GetAvatarAndTextColor = (name) => {
    // List of background colors (tailwind colors)
    const bgColors = [
        'bg-blue-50', 'bg-green-50', 'bg-yellow-50', 'bg-red-50',
        'bg-purple-50', 'bg-pink-50', 'bg-indigo-50', 'bg-orange-50',
        'bg-teal-50', 'bg-cyan-50'
    ];

    // Corresponding text colors for contrast
    const textColors = [
        'text-blue-700', 'text-green-700', 'text-yellow-700', 'text-red-700',
        'text-purple-700', 'text-pink-700', 'text-indigo-700', 'text-orange-700',
        'text-teal-700', 'text-cyan-700'
    ];

    // Simple hash function to get consistent color for the same name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use the hash to pick a color from our arrays
    const index = Math.abs(hash) % bgColors.length;

    return {
        bg: bgColors[index],
        text: textColors[index]
    };
};

export const SupportTicketStatusColumns = {
    [SUPPORT_TICKET_STATUSES.OPEN]: {
        color: 'blue',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: AlertCircleIcon,
        label: 'Open',
        description: 'New tickets'
    },

    [SUPPORT_TICKET_STATUSES.IN_PROGRESS]: {
        color: 'orange',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: ClockIcon,
        label: 'In Progress',
        description: 'Being worked on'
    },
    [SUPPORT_TICKET_STATUSES.PENDING_CUSTOMER]: {
        color: 'purple',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        icon: MessageSquareIcon,
        label: 'Pending',
        description: 'Waiting for response'
    },
    [SUPPORT_TICKET_STATUSES.RESOLVED]: {
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircleIcon,
        label: 'Resolved',
        description: 'Resolved'
    },
    [SUPPORT_TICKET_STATUSES.CLOSED]: {
        color: 'gray',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        icon: XCircleIcon,
        label: 'Closed',
        description: 'Closed'
    },
    [SUPPORT_TICKET_STATUSES.CANCELLED]: {
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: XCircleIcon,
        label: 'Cancelled',
        description: 'Cancelled'
    },
    [SUPPORT_TICKET_STATUSES.AWAITING_APPROVAL]: {
        color: 'orange',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: ClockIcon,
        label: 'Awaiting Approval',
        description: 'Pending head approval'
    }
};

export const SupportTicketStatusConfig = (status) => {
    const configs = SupportTicketStatusColumns;
    return configs[status] || configs[SUPPORT_TICKET_STATUSES.OPEN];
};

export const SupportTicketPriorityConfig = (priority) => {
    const configs = {
        [SUPPORT_TICKET_PRIORITIES.LOW]: { color: 'bg-green-100 text-green-700 border-green-200', icon: InfoIcon, label: 'Low' },
        [SUPPORT_TICKET_PRIORITIES.MEDIUM]: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertTriangleIcon, label: 'Medium' },
        [SUPPORT_TICKET_PRIORITIES.HIGH]: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircleIcon, label: 'High' },
        [SUPPORT_TICKET_PRIORITIES.URGENT]: { color: 'bg-red-100 text-red-700 border-red-200', icon: ZapIcon, label: 'Urgent' }
    };
    return configs[priority] || configs[SUPPORT_TICKET_PRIORITIES.MEDIUM];
};

export const SupportTicketCategoryIcon = (category) => {
    // Categories are admin-configurable, so this can only ever cover the seeded set.
    // Anything an admin adds later falls back to the generic tag rather than breaking.
    const icons = {
        safety_security: XCircleIcon,
        power: SettingsIcon,
        internet: SettingsIcon,
        cleanliness: MessageSquareIcon,
        staff_service: UserIcon,
        supplies: TagIcon,
        access_membership: UserIcon,
        furniture: TagIcon,
        comfort: SettingsIcon,
        cafe_kitchen: TagIcon,
        meeting_rooms: MessageSquareIcon,
        billing: TrendingUpIcon,
        general: MessageSquareIcon,
        new_request: PlusIcon,
    };
    return icons[category] || TagIcon;
};

export const InterpretVerificationStatus = (status, type = "chip") => {
    switch (status) {
        case VERIFICATION_TYPES.APPROVED:
            return type === "chip" ? <Chip color="green" size="sm" variant="ghost" value="Approved" /> : <span className="text-green-700 text-xs">Approved</span>;
        case VERIFICATION_TYPES.PENDING:
            return type === "chip" ? <Chip color="yellow" size="sm" variant="ghost" value="Pending" /> : <span className="text-yellow-700 text-xs">Pending</span>;
        case VERIFICATION_TYPES.NOTIFIED:
            return type === "chip" ? <Chip color="blue-gray" size="sm" variant="ghost" value="Notified" /> : <span className="text-blue-gray-700 text-xs">Notified</span>;
        case VERIFICATION_TYPES.REJECTED:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="Rejected" /> : <span className="text-red-700 text-xs">Rejected</span>;
        case VERIFICATION_TYPES.CANCELLED:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Cancelled" /> : <span className="text-gray-500 text-xs">Cancelled</span>;
        case VERIFICATION_TYPES.COMPLETED:
            return type === "chip" ? <Chip color="blue" size="sm" variant="ghost" value="Completed" /> : <span className="text-blue-700 text-xs">Completed</span>;
        default:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Unknown" /> : <span className="text-gray-500 text-xs">Unknown</span>;
    }
};

export const InterpretDayPassType = (type) => {
    switch (type) {
        case DAY_PASSES_TYPES.HOURLY:
            return <Chip color="purple" size="sm" variant="ghost" value="Hourly" />;
        case DAY_PASSES_TYPES.DAILY:
            return <Chip color="blue" size="sm" variant="ghost" value="Daily" />;
        case DAY_PASSES_TYPES.MONTHLY_RECURRING:
            return <Chip color="green" size="sm" variant="ghost" value="Monthly" />;
        default:
            return <Chip color="gray" size="sm" variant="ghost" value="Unknown" />;
    }
};

export const InterpretPaymentStatus = (status, type = "chip") => {
    switch (status) {
        case PAYMENT_STATUS.PAID:
            return type === "chip" ? <Chip color="green" size="sm" variant="ghost" value="Paid" /> : <span className="text-green-700 text-xs">Paid</span>;
        case PAYMENT_STATUS.PENDING:
            return type === "chip" ? <Chip color="yellow" size="sm" variant="ghost" value="Pending" /> : <span className="text-yellow-700 text-xs">Pending</span>;
        case PAYMENT_STATUS.FAILED:
            return type === "chip" ? <Chip color="red" size="sm" variant="ghost" value="Failed" /> : <span className="text-red-700 text-xs">Failed</span>;
        case PAYMENT_STATUS.REFUNDED:
            return type === "chip" ? <Chip color="purple" size="sm" variant="ghost" value="Refunded" /> : <span className="text-purple-700 text-xs">Refunded</span>;
        default:
            return type === "chip" ? <Chip color="gray" size="sm" variant="ghost" value="Unknown" /> : <span className="text-gray-500 text-xs">Unknown</span>;
    }
};